import { throttle } from "lodash-es";
import { makeAutoObservable, reaction } from "mobx";
import { findElement } from "../lib/element";

export type Selector = { target: string };

export class SelectorObserver {
  matches: Record<string, { element: Element }> = {};
  private selectors: Selector[] = [];
  private selectorSearchStartTimes: Map<string, number> = new Map();
  checkInterval: number = 1000;

  constructor() {
    makeAutoObservable(this);

    reaction(
      () => this.selectors,
      () => {
        // FIX: this is called after isAvailable is called
        // This causes selector search not started error on initial isAvailable call
        this.findAllSelectors();
      }
    );

    this.observe();
  }

  update(selectors: Selector[]) {
    this.selectors = selectors;
  }

  get foundSelectors() {
    return Object.keys(this.matches);
  }

  findThisElementsThrottled = throttle(() => {
    this.findAllSelectors();
  }, 100);

  findAllSelectors() {
    for (const selector of this.selectors) {
      this.findSelector(selector.target);
    }
  }

  findSelector(selector: string) {
    const now = Date.now();
    if (!this.selectorSearchStartTimes.has(selector)) {
      this.selectorSearchStartTimes.set(selector, now);
    }

    const el = findElement(selector);
    if (el) {
      this.matches[selector] = { element: el };
    } else {
      if (this.matches[selector]) {
        delete this.matches[selector];
      }
    }
  }

  observe() {
    this.findThisElementsThrottled();

    const observer = new MutationObserver(() => {
      this.findThisElementsThrottled();
    });

    observer.observe(document, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    const intervalId = setInterval(() => {
      this.findThisElementsThrottled();
    }, this.checkInterval);

    return () => {
      // cleanup
      observer.disconnect();
      clearInterval(intervalId);
    };
  }

  isSelectorFound(selector: string) {
    return !!this.matches[selector];
  }

  waitUntilSelectorFound(selector: string, timeout: number): Promise<Element | null> {
    return new Promise((resolve, _reject) => {
      const startTime = Date.now();
      const intervalId = setInterval(() => {
        if (this.isSelectorFound(selector)) {
          clearInterval(intervalId);
          resolve(this.matches[selector].element);
        }

        const elapsedTime = Date.now() - startTime;
        if (elapsedTime > timeout) {
          clearInterval(intervalId);
          resolve(null);
        }
      }, 100);
    });
  }

  async isAvailable(selector: string, timeout: number): Promise<boolean> {
    if (this.isSelectorFound(selector)) {
      return Promise.resolve(true);
    }

    const startTime = this.selectorSearchStartTimes.get(selector);
    const isSearchNotStartedYet = !startTime;
    if (isSearchNotStartedYet) {
      this.findSelector(selector);
      await this.waitUntilSelectorFound(selector, timeout);
      return Promise.resolve(this.isSelectorFound(selector));
    }

    const elapsedTime = Date.now() - startTime;
    const remainingTime = timeout - elapsedTime;

    const isTimeoutPassed = remainingTime <= 0;
    if (isTimeoutPassed) {
      return Promise.resolve(this.isSelectorFound(selector));
    }

    await this.waitUntilSelectorFound(selector, remainingTime);
    return this.isSelectorFound(selector);
  }
}
